import { Box } from '@mui/material';
import homeMessages from 'client/modules/Public/pages/HomePage/homeMessages';
import Formsy from 'formsy-react';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import Typed from 'typed.js';
import BrowserWindow from '../../../../../App/components/BrowserWindow/BrowserWindow';
import Message from '../../../../../App/components/Message/Message';
import ResponsiveImage from '../../../../../App/components/ResponsiveImage/ResponsiveImage';
import { loginError, loginUser } from '../../../../../Auth/AuthActions';
import { getErrorMessage } from '../../../../../Auth/AuthReducer';
import { loginMessages } from '../../../../../Auth/pages/Login/components/LoginWidget/LoginWidget';
import {
  homeAcceptTermsSx,
  homeColMd4Sx,
  homeColMd5Sx,
  homeColMd6Sx,
  homeColMd7Sx,
  homeFormTitleSx,
  homeHeroRowReverseSx,
  homeHeroRowSx,
  homeHeroTextSx,
  homeHeroTitleSx,
  homeIntroSx,
  homeSurveyTypeSx,
  marketingBtnPrimarySx,
  marketingContainerSx,
  marketingPanelBodySx,
  marketingPanelSx,
} from '../../homeSsrStyles';
import HomeInput from './components/HomeInput';
import snap1000 from './images/snap-1000.png';
import snap2018 from './images/snap-2018.png';
import snap500 from './images/snap-500.png';
import placeholder from './images/snap-placeholder.jpg';

const messages = defineMessages({
  screenShotAlt: { id: 'Home.ScreenshotAlt', defaultMessage: 'Promoter Ninja screenshot' },
});

function Home({ surveyType, signUp, errorMessage, onCloseError }) {
  const el = React.useRef(null);
  const typed = useRef(null);

  useEffect(() => {
    if (!surveyType) {
      el.current.innerText = '';
      typed.current = new Typed(el.current, {
        strings: ['NPS', 'CSAT', '5-star', 'CES'],
        typeSpeed: 80,
        backSpeed: 40,
        backDelay: 2000,
        loop: true,
        startDelay: 0,
        smartBackspace: false,
      });
    }
    return () => {
      if (typed.current) {
        typed.current.destroy();
      }
    };
  }, [surveyType]);

  useEffect(() => {
    if (global.window && global.window.location.hash) {
      const id = global.window.location.hash.replace('#', '');
      const elem = global.document.getElementById(id);
      if (elem) {
        elem.scrollIntoView();
      }
    }
  }, []);

  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const longType = surveyType ? formatMessage(homeMessages[surveyType]) : '';
  const shortType = surveyType ? formatMessage(homeMessages[`${surveyType}Short`]) : '';

  return (
    <Box sx={{ position: 'relative' }}>
      <Box sx={homeIntroSx} id="intro">
        <Box sx={marketingContainerSx}>
          <Box sx={homeHeroRowSx}>
            <Box sx={homeColMd6Sx}>
              <Box component="h3" sx={homeHeroTitleSx}>
                <strong>
                  <FormattedMessage
                    id="Home.ClaimGeneric"
                    defaultMessage="The easiest {type} survey in the market"
                    values={{
                      type: surveyType ? (
                        shortType
                      ) : (
                        <Box component="span" sx={homeSurveyTypeSx}>
                          <span ref={el}>NPS</span>
                        </Box>
                      ),
                    }}
                  />
                </strong>
              </Box>
              <Box component="p" sx={homeHeroTextSx}>
                <FormattedMessage
                  id="Home.SubClaimGeneric"
                  defaultMessage="Send {longType} surveys to your customers and gather actionable feedback."
                  values={{ longType }}
                />
              </Box>
            </Box>
            <Box sx={homeColMd5Sx}>
              <Box sx={marketingPanelSx}>
                <Box sx={marketingPanelBodySx}>
                  <Box sx={homeFormTitleSx}>
                    <FormattedMessage
                      id="Home.SubClaim2"
                      defaultMessage="Send now your first 500 surveys for free."
                    />
                  </Box>
                  {errorMessage ? (
                    <Message
                      type="error"
                      msg={errorMessage}
                      onClose={onCloseError}
                      sticky={false}
                    />
                  ) : null}
                  <Formsy.Form onValidSubmit={creds => signUp(creds, navigate)}>
                    <HomeInput
                      id="email"
                      name="email"
                      validations="isEmail"
                      validationError={formatMessage(loginMessages.invalidEmail)}
                      placeholder={formatMessage(loginMessages.email)}
                      required
                      autoComplete="email"
                      autoFocus
                    />
                    <HomeInput
                      id="password"
                      name="password"
                      type="password"
                      placeholder={formatMessage(loginMessages.password)}
                      required
                      autoComplete="password"
                    />
                    <Box
                      component="button"
                      type="submit"
                      sx={{ ...marketingBtnPrimarySx, fontSize: 18, width: '100%' }}
                    >
                      <FormattedMessage id="Home.CallToAction" defaultMessage="Get Started" />
                    </Box>
                  </Formsy.Form>
                  <Box component="span" sx={homeAcceptTermsSx}>
                    <FormattedMessage
                      id="Home.AgreeTerms"
                      defaultMessage="By creating an account you agree to our {terms} & {privacy}."
                      values={{
                        terms: (
                          <Link to={{ pathname: '/legal/terms' }}>
                            <FormattedMessage id="Home.Terms" defaultMessage="Terms" />
                          </Link>
                        ),
                        privacy: (
                          <Link to={{ pathname: '/legal/privacy' }}>
                            <FormattedMessage id="Home.Privacy" defaultMessage="Privacy Policy" />
                          </Link>
                        ),
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box sx={homeHeroRowReverseSx}>
            <Box sx={homeColMd4Sx}>
              <Box component="h3" sx={homeHeroTitleSx}>
                <FormattedMessage
                  id="Home.SegmentWithPropertiesTitle"
                  defaultMessage="Segmented analysis with just one click"
                />
              </Box>
              <Box component="p" sx={homeHeroTextSx}>
                <FormattedMessage
                  id="Home.SegmentWithPropertiesDescription"
                  defaultMessage="Just add some columns to the CSV you upload and we will automatically create filters for you. See NPS differences by product, geography or any other property you define."
                />
              </Box>
            </Box>
            <Box sx={homeColMd7Sx}>
              <BrowserWindow>
                <ResponsiveImage
                  sizes="(max-width: 767px) 100vw, 55vw"
                  placeholder={placeholder}
                  srcSet={{
                    500: snap500,
                    1000: snap1000,
                    2018: snap2018,
                  }}
                  alt={formatMessage(messages.screenShotAlt)}
                />
              </BrowserWindow>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

const types = ['nps', 'ces', 'five-star', 'csat'];

const mapStateToProps = state => ({
  errorMessage: getErrorMessage(state),
});

const mapDispatchToProps = dispatch => ({
  signUp(creds, navigate) {
    dispatch(loginUser(creds, true, navigate));
  },
  onCloseError() {
    dispatch(loginError(null));
  },
});

Home.defaultProps = {
  surveyType: null,
  errorMessage: null,
};

Home.propTypes = {
  signUp: PropTypes.func.isRequired,
  surveyType: PropTypes.oneOf(types),
  errorMessage: PropTypes.string,
  onCloseError: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Home);
